import { useCallback, useEffect, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import { saveAs } from "file-saver";
import MediaLiveService from "../services/medialive.service";

const TRANSITORY_STATES = [
  "STARTING",
  "STOPPING",
  "RECOVERING",
  "UPDATING",
  "CREATING",
  "DELETING",
];

const POLL_INTERVAL_TERMINAL = 10000;
const POLL_INTERVAL_TRANSITORY = 3000;
const MP4_POLL_INTERVAL = 5000;
const MP4_IN_PROGRESS_STATES = ["SUBMITTED", "PROGRESSING"];

export function useStream() {
  const [channel, setChannel] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoader, setActionLoader] = useState(false);
  const [error, setError] = useState(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [confirmStopOpen, setConfirmStopOpen] = useState(false);

  // MP4 del ultimo live
  const [mp4Loading, setMp4Loading] = useState(false);
  const [mp4Status, setMp4Status] = useState(null);
  const [mp4JobId, setMp4JobId] = useState(null);
  const [mp4DownloadUrl, setMp4DownloadUrl] = useState(null);
  const [mp4LiveTitle, setMp4LiveTitle] = useState(null);
  const [mp4ProgressPct, setMp4ProgressPct] = useState(null);
  const [mp4Downloading, setMp4Downloading] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const prevStateRef = useRef(null);
  const intervalRef = useRef(null);
  const mp4IntervalRef = useRef(null);
  const isVisibleRef = useRef(
    typeof document !== "undefined"
      ? document.visibilityState !== "hidden"
      : true
  );
  const isFetchingRef = useRef(false);

  const state = channel?.state;
  const isTransitory = TRANSITORY_STATES.includes(state);

  const canStart = state === "IDLE" && !actionLoader;
  const canStop =
    (state === "RUNNING" || state === "RECOVERING") && !actionLoader;

  const fetchAll = useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    try {
      const [describeRes, metricsRes] = await Promise.allSettled([
        MediaLiveService.describe(),
        MediaLiveService.metrics(),
      ]);

      if (describeRes.status === "fulfilled") {
        const payload = describeRes.value?.data ?? describeRes.value;
        setChannel(payload);
        setError(null);

        const prev = prevStateRef.current;
        const next = payload?.state;
        if (
          prev &&
          TRANSITORY_STATES.includes(prev) &&
          next &&
          !TRANSITORY_STATES.includes(next)
        ) {
          if (next === "RUNNING") {
            enqueueSnackbar("Canal en ejecucion", { variant: "success" });
          } else if (next === "IDLE") {
            enqueueSnackbar("Canal detenido", { variant: "success" });
          }
        }
        prevStateRef.current = next;
      } else {
        setError(describeRes.reason);
        enqueueSnackbar("Error al obtener el estado del canal", {
          variant: "error",
        });
      }

      if (metricsRes.status === "fulfilled") {
        const payload = metricsRes.value?.data ?? metricsRes.value;
        setMetrics(payload);
      } else {
        setMetrics(null);
      }

      setLastRefreshedAt(Date.now());
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  const scheduleInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (!isVisibleRef.current) return;

    const delay = isTransitory
      ? POLL_INTERVAL_TRANSITORY
      : POLL_INTERVAL_TERMINAL;

    intervalRef.current = setInterval(() => {
      if (!isVisibleRef.current) return;
      fetchAll();
    }, delay);
  }, [isTransitory, fetchAll]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    scheduleInterval();
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [scheduleInterval]);

  useEffect(() => {
    const handleVisibility = () => {
      const visible = document.visibilityState !== "hidden";
      isVisibleRef.current = visible;
      if (visible) {
        fetchAll();
        scheduleInterval();
      } else if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [fetchAll, scheduleInterval]);

  const refresh = useCallback(async () => {
    await fetchAll();
  }, [fetchAll]);

  const onStart = useCallback(async () => {
    setActionLoader(true);
    try {
      await MediaLiveService.start();
      enqueueSnackbar("Solicitud de inicio enviada", { variant: "info" });
      await fetchAll();
    } catch (err) {
      enqueueSnackbar("No se pudo iniciar el canal", { variant: "error" });
    } finally {
      setActionLoader(false);
    }
  }, [fetchAll, enqueueSnackbar]);

  const onStop = useCallback(async () => {
    setActionLoader(true);
    try {
      await MediaLiveService.stop();
      enqueueSnackbar("Solicitud de detencion enviada", { variant: "info" });
      await fetchAll();
      setConfirmStopOpen(false);
    } catch (err) {
      enqueueSnackbar("No se pudo detener el canal", { variant: "error" });
    } finally {
      setActionLoader(false);
    }
  }, [fetchAll, enqueueSnackbar]);

  const clearMp4Interval = useCallback(() => {
    if (mp4IntervalRef.current) {
      clearInterval(mp4IntervalRef.current);
      mp4IntervalRef.current = null;
    }
  }, []);

  const pollMp4Status = useCallback(
    async (jobId) => {
      try {
        const res = await MediaLiveService.lastLiveMp4Status(jobId);
        const payload = res?.data ?? res;
        const status = payload?.status ?? null;
        setMp4Status(status);
        setMp4ProgressPct(
          payload?.progressPct != null ? payload.progressPct : null
        );

        if (status === "COMPLETE") {
          clearMp4Interval();
          setMp4DownloadUrl(payload?.downloadUrl ?? null);
          setMp4Loading(false);
          enqueueSnackbar("MP4 listo para descargar", { variant: "success" });
        } else if (status === "ERROR" || status === "CANCELED") {
          clearMp4Interval();
          setMp4Loading(false);
          enqueueSnackbar(
            status === "ERROR"
              ? "Error al generar el MP4"
              : "La generacion del MP4 fue cancelada",
            { variant: "error" }
          );
        }
      } catch (err) {
        clearMp4Interval();
        setMp4Loading(false);
        setMp4Status("ERROR");
        enqueueSnackbar("Error al consultar el estado del MP4", {
          variant: "error",
        });
      }
    },
    [enqueueSnackbar, clearMp4Interval]
  );

  const onGenerateMp4 = useCallback(async () => {
    setMp4Loading(true);
    setMp4DownloadUrl(null);
    setMp4ProgressPct(null);
    setMp4Status("SUBMITTED");
    clearMp4Interval();
    try {
      const res = await MediaLiveService.generateLastLiveMp4();
      const payload = res?.data ?? res;
      const jobId = payload?.jobId ?? null;
      setMp4JobId(jobId);
      setMp4LiveTitle(payload?.title ?? null);

      if (!jobId) {
        throw new Error("No se recibio el jobId de la conversion");
      }

      enqueueSnackbar("Generacion de MP4 iniciada", { variant: "info" });

      // Primer chequeo inmediato + polling cada ~5s
      await pollMp4Status(jobId);
      mp4IntervalRef.current = setInterval(() => {
        pollMp4Status(jobId);
      }, MP4_POLL_INTERVAL);
    } catch (err) {
      clearMp4Interval();
      setMp4Loading(false);
      setMp4Status("ERROR");
      enqueueSnackbar("No se pudo iniciar la generacion del MP4", {
        variant: "error",
      });
    }
  }, [enqueueSnackbar, pollMp4Status, clearMp4Interval]);

  const onDownloadMp4 = useCallback(() => {
    if (!mp4DownloadUrl) {
      enqueueSnackbar("El MP4 todavia no esta disponible", {
        variant: "warning",
      });
      return;
    }
    try {
      setMp4Downloading(true);
      const fileName = `live-${mp4JobId || "ultimo"}.mp4`;
      saveAs(mp4DownloadUrl, fileName);
      enqueueSnackbar("Descarga iniciada", { variant: "success" });
    } catch (err) {
      enqueueSnackbar("No se pudo iniciar la descarga", { variant: "error" });
    } finally {
      setMp4Downloading(false);
    }
  }, [mp4DownloadUrl, mp4JobId, enqueueSnackbar]);

  // Limpieza del interval de MP4 al desmontar
  useEffect(() => {
    return () => {
      clearMp4Interval();
    };
  }, [clearMp4Interval]);

  const mp4InProgress = MP4_IN_PROGRESS_STATES.includes(mp4Status);
  const mp4Ready = mp4Status === "COMPLETE" && !!mp4DownloadUrl;

  return {
    channel,
    metrics,
    loading,
    actionLoader,
    error,
    lastRefreshedAt,
    canStart,
    canStop,
    isTransitory,
    confirmStopOpen,
    setConfirmStopOpen,
    refresh,
    onStart,
    onStop,
    // MP4 del ultimo live
    mp4Loading,
    mp4Status,
    mp4JobId,
    mp4DownloadUrl,
    mp4LiveTitle,
    mp4ProgressPct,
    mp4Downloading,
    mp4InProgress,
    mp4Ready,
    onGenerateMp4,
    onDownloadMp4,
  };
}

export default useStream;
